<template>
  <div class="tag-update-post">
    <h1>Tag 정보 업데이트</h1>
    <p class="company-info">Company: <strong>{{ company }}</strong></p>
    <p class="hint">QR 코드(tag_mac) → 바코드(롤테이너 ID) 순서로 스캔하세요. (1~100개)</p>

    <!-- 상단 스캔 영역 (1개) -->
    <div class="scan-area">
      <div class="field-group">
        <label>Tag MAC (QR)</label>
        <div class="input-with-scan">
          <input
            v-model="scanInput.tag_mac"
            type="text"
            placeholder="QR 스캔 또는 입력"
            @keyup.enter="focusDevicename"
          />
          <button type="button" class="btn-scan" @click="openScanner('tag_mac')">QR</button>
        </div>
      </div>
      <div class="field-group">
        <label>Devicename (바코드)</label>
        <div class="input-with-scan">
          <input
            ref="devicenameInputRef"
            v-model="scanInput.devicename"
            type="text"
            placeholder="바코드 스캔 또는 입력"
            @keyup.enter="addFromScanArea"
          />
          <button type="button" class="btn-scan" @click="openScanner('devicename')">바코드</button>
        </div>
      </div>
      <button type="button" class="btn-add-row" @click="addFromScanArea" :disabled="!canAdd">
        행 추가
      </button>
    </div>

    <!-- 스캔 결과 표 -->
    <div class="table-section">
      <h3>스캔 목록 ({{ tableRows.length }}건)</h3>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Tag MAC</th>
              <th>Devicename</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableRows" :key="index">
              <td>{{ index + 1 }}</td>
              <td>{{ row.tag_mac }}</td>
              <td>{{ row.devicename }}</td>
              <td>
                <button type="button" class="btn-remove" @click="removeRow(index)">삭제</button>
              </td>
            </tr>
            <tr v-if="tableRows.length === 0">
              <td colspan="4" class="empty-msg">스캔한 항목이 없습니다.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="actions">
      <button type="button" class="btn-save" @click="save" :disabled="saving || tableRows.length === 0">
        {{ saving ? '저장 중...' : '저장' }}
      </button>
    </div>

    <!-- 스캐너 모달 -->
    <div v-if="showScanner" class="scanner-modal">
      <div class="scanner-content">
        <h3>{{ scanTarget === 'tag_mac' ? 'QR 코드 스캔' : '바코드 스캔' }}</h3>
        <div id="qr-reader" class="qr-reader" @click="onScannerAreaTap"></div>
        <div v-if="zoomSupported" class="zoom-controls">
          <button type="button" class="btn-zoom" @click="zoomOut">−</button>
          <span class="zoom-label">{{ zoomLevel }}x</span>
          <button type="button" class="btn-zoom" @click="zoomIn">+</button>
        </div>
        <p class="scan-hint">
          {{ zoomSupported ? '줌 +/- 로 크기 조절 · ' : '' }}{{ focusHint }}
        </p>
        <button type="button" class="btn-close" @click="closeScanner">닫기</button>
      </div>
    </div>

    <!-- 결과/중복 팝업 -->
    <div v-if="showPopup" class="popup">
      <div class="popup-content">
        <h3>{{ popupMessage }}</h3>
        <button @click="closePopup">확인</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// QR 전용 (tag_mac), 1D 바코드 전용 (devicename) - 서로 인식 안 되도록 분리
const QR_ONLY = { formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE] };
const BARCODE_ONLY = {
  formatsToSupport: [
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.CODE_93,
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.CODABAR,
    Html5QrcodeSupportedFormats.ITF,
  ],
};

const MAX_ROWS = 100;

export default {
  name: 'TagUpdatePost',
  data() {
    return {
      scanInput: { tag_mac: '', devicename: '' },
      tableRows: [],
      showScanner: false,
      scanTarget: null,
      html5QrCode: null,
      showPopup: false,
      popupMessage: '',
      saving: false,
      zoomSupported: false,
      zoomLevel: 1,
      zoomMin: 1,
      zoomMax: 1,
      zoomStep: 0.25,
      videoTrack: null,
      focusHint: '멀리서 스캔 시 가까이서 다시 시도해보세요',
    };
  },
  computed: {
    company() {
      return localStorage.getItem('company') || '';
    },
    userEmail() {
      return localStorage.getItem('email') || '';
    },
    canAdd() {
      const tm = (this.scanInput.tag_mac || '').trim();
      const dn = (this.scanInput.devicename || '').trim();
      return tm && dn;
    },
  },
  beforeUnmount() {
    this.closeScanner();
  },
  methods: {
    focusDevicename() {
      this.$refs.devicenameInputRef?.focus();
    },
    checkDuplicate(tag_mac, devicename) {
      const tm = (tag_mac || '').trim();
      const dn = (devicename || '').trim();
      const tagMacDup = this.tableRows.some((r) => (r.tag_mac || '').trim() === tm);
      const devicenameDup = this.tableRows.some((r) => (r.devicename || '').trim() === dn);
      if (tagMacDup) return { duplicate: true, field: 'tag_mac' };
      if (devicenameDup) return { duplicate: true, field: 'devicename' };
      return { duplicate: false };
    },
    addFromScanArea() {
      const tm = (this.scanInput.tag_mac || '').trim();
      const dn = (this.scanInput.devicename || '').trim();
      if (!tm || !dn) return;
      if (this.tableRows.length >= MAX_ROWS) {
        this.popupMessage = `최대 ${MAX_ROWS}개까지 입력 가능합니다.`;
        this.showPopup = true;
        return;
      }
      const dup = this.checkDuplicate(tm, dn);
      if (dup.duplicate) {
        this.popupMessage =
          dup.field === 'tag_mac'
            ? 'tag_mac이 이미 스캔되었습니다. 스캔이 잘못되었을 수 있습니다.'
            : 'devicename이 이미 스캔되었습니다. 스캔이 잘못되었을 수 있습니다.';
        this.showPopup = true;
        return;
      }
      this.tableRows.push({ tag_mac: tm, devicename: dn });
      this.scanInput.tag_mac = '';
      this.scanInput.devicename = '';
    },
    removeRow(index) {
      this.tableRows.splice(index, 1);
    },
    openScanner(target) {
      this.scanTarget = target;
      this.showScanner = true;
      this.$nextTick(() => this.startScanner());
    },
    async startScanner() {
      const el = document.getElementById('qr-reader');
      if (!el) return;
      const onSuccess = (decodedText) => this.onScanSuccess(decodedText);
      const onError = () => {};
      const formats = this.scanTarget === 'tag_mac' ? QR_ONLY : BARCODE_ONLY;
      try {
        this.html5QrCode = new Html5Qrcode('qr-reader', formats);
        await this.html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 280, height: 280 } },
          onSuccess,
          onError
        );
      } catch (err) {
        console.error('Scanner start failed:', err);
        const msg = err?.name === 'NotAllowedError' || err?.message?.includes('Permission')
          ? '카메라 접근 권한을 허용해주세요.'
          : (err?.message || String(err)).slice(0, 80);
        this.popupMessage = msg;
        this.showPopup = true;
        this.closeScanner();
      }
      this.setupZoomAndFocus();
    },
    closeScanner() {
      if (this.html5QrCode && this.html5QrCode.isScanning) {
        this.html5QrCode.stop().catch(() => {});
      }
      this.html5QrCode = null;
      this.videoTrack = null;
      this.zoomSupported = false;
      this.showScanner = false;
      this.scanTarget = null;
      const el = document.getElementById('qr-reader');
      if (el) el.innerHTML = '';
    },
    getVideoTrack() {
      const el = document.getElementById('qr-reader');
      const video = el?.querySelector('video');
      if (video?.srcObject) {
        const tracks = video.srcObject.getVideoTracks();
        return tracks.length ? tracks[0] : null;
      }
      return null;
    },
    setupZoomAndFocus() {
      this.$nextTick(() => {
        const h5 = this.html5QrCode;
        if (!h5?.isScanning) return;
        try {
          const cap = h5.getRunningTrackCapabilities?.();
          const settings = h5.getRunningTrackSettings?.();
          if (cap && typeof cap.zoom === 'object') {
            this.zoomMin = cap.zoom?.min ?? 1;
            this.zoomMax = cap.zoom?.max ?? 4;
            this.zoomStep = cap.zoom?.step ?? 0.25;
            this.zoomLevel = settings?.zoom ?? this.zoomMin;
            this.zoomSupported = true;
          }
          this.videoTrack = this.getVideoTrack();
        } catch (e) {
          console.warn('Zoom/focus setup:', e);
        }
      });
    },
    async zoomIn() {
      if (!this.zoomSupported || !this.html5QrCode) return;
      const next = Math.min(this.zoomLevel + this.zoomStep, this.zoomMax);
      try {
        await this.html5QrCode.applyVideoConstraints?.({ advanced: [{ zoom: next }] });
        this.zoomLevel = next;
      } catch (e) {
        console.warn('Zoom apply failed:', e);
      }
    },
    async zoomOut() {
      if (!this.zoomSupported || !this.html5QrCode) return;
      const next = Math.max(this.zoomLevel - this.zoomStep, this.zoomMin);
      try {
        await this.html5QrCode.applyVideoConstraints?.({ advanced: [{ zoom: next }] });
        this.zoomLevel = next;
      } catch (e) {
        console.warn('Zoom apply failed:', e);
      }
    },
    async onScannerAreaTap() {
      const track = this.videoTrack || this.getVideoTrack();
      if (!track) return;
      const cap = track.getCapabilities?.();
      if (cap?.focusMode) {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: 'manual', focusDistance: 0.5 }],
          });
          this.focusHint = '초점 조정됨';
          setTimeout(() => (this.focusHint = '화면 탭 시 초점 시도'), 2000);
        } catch (e) {
          this.focusHint = '이 기기에서는 초점 조정 미지원';
        }
      }
    },
    onScanSuccess(decodedText) {
      const val = (decodedText || '').trim();
      if (!val) return;
      const target = this.scanTarget; // closeScanner()가 scanTarget을 null로 만들기 전에 저장
      this.closeScanner();
      if (target === 'tag_mac') {
        this.scanInput.tag_mac = val;
        this.$nextTick(() => this.$refs.devicenameInputRef?.focus());
      } else {
        this.scanInput.devicename = val;
        this.$nextTick(() => this.addFromScanArea());
      }
    },
    async save() {
      const company = (this.company || '').toLowerCase();
      if (company !== 'post') {
        this.popupMessage = 'POST 회사만 사용할 수 있습니다.';
        this.showPopup = true;
        return;
      }
      const items = this.tableRows.map((r) => ({
        tag_mac: (r.tag_mac || '').trim(),
        devicename: (r.devicename || '').trim(),
      }));
      if (items.length === 0) {
        this.popupMessage = '최소 1개 이상 스캔 후 저장해주세요.';
        this.showPopup = true;
        return;
      }
      // 저장 전 중복 최종 확인
      const tagMacSet = new Set();
      const devicenameSet = new Set();
      for (const it of items) {
        if (tagMacSet.has(it.tag_mac)) {
          this.popupMessage = `tag_mac "${it.tag_mac}" 중복입니다. 스캔이 잘못되었을 수 있습니다.`;
          this.showPopup = true;
          return;
        }
        if (devicenameSet.has(it.devicename)) {
          this.popupMessage = `devicename "${it.devicename}" 중복입니다. 스캔이 잘못되었을 수 있습니다.`;
          this.showPopup = true;
          return;
        }
        tagMacSet.add(it.tag_mac);
        devicenameSet.add(it.devicename);
      }
      this.saving = true;
      try {
        const res = await axios.post('/api/tagUpdatePost', {
          company: this.company,
          user_email: this.userEmail || null,
          items,
        });
        this.popupMessage = res.data.message || `${items.length}건 업데이트 완료`;
        this.showPopup = true;
        this.tableRows = [];
        this.scanInput = { tag_mac: '', devicename: '' };
      } catch (err) {
        this.popupMessage = err.response?.data?.error || err.message || '저장 실패';
        this.showPopup = true;
      } finally {
        this.saving = false;
      }
    },
    closePopup() {
      this.showPopup = false;
    },
  },
};
</script>

<style scoped>
.tag-update-post {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 80px;
}

h1 {
  font-size: 1.25rem;
  margin-bottom: 8px;
}

.company-info {
  color: #666;
  margin-bottom: 4px;
}

.hint {
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 16px;
}

.scan-area {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.field-group {
  margin-bottom: 10px;
}

.field-group:last-of-type {
  margin-bottom: 12px;
}

.field-group label {
  display: block;
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 4px;
}

.input-with-scan {
  display: flex;
  gap: 8px;
}

.input-with-scan input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
}

.btn-scan {
  padding: 10px 14px;
  background: #2196f3;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
}

.btn-scan:hover {
  background: #1976d2;
}

.btn-add-row {
  width: 100%;
  padding: 10px;
  background: #4caf50;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-add-row:hover:not(:disabled) {
  background: #43a047;
}

.btn-add-row:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-section {
  margin-bottom: 16px;
}

.table-section h3 {
  font-size: 0.95rem;
  margin: 0 0 8px 0;
  color: #333;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.data-table th,
.data-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.data-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #555;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.empty-msg {
  color: #999;
  text-align: center;
  padding: 24px;
}

.btn-remove {
  padding: 4px 10px;
  background: #f44336;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-remove:hover {
  background: #d32f2f;
}

.actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}

.btn-save {
  width: 100%;
  padding: 14px;
  background: #4caf50;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.scanner-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.scanner-content {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  width: 100%;
  max-width: 360px;
}

.scanner-content h3 {
  margin: 0 0 12px 0;
  font-size: 1rem;
}

.qr-reader {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.qr-reader video {
  width: 100%;
}

.zoom-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
}

.btn-zoom {
  width: 44px;
  height: 44px;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  background: #2196f3;
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-zoom:hover {
  background: #1976d2;
}

.zoom-label {
  font-size: 0.9rem;
  color: #333;
  min-width: 48px;
  text-align: center;
}

.scan-hint {
  font-size: 0.8rem;
  color: #666;
  margin: 8px 0 0 0;
}

.btn-close {
  margin-top: 12px;
  width: 100%;
  padding: 10px;
  background: #666;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.popup {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.popup-content {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 320px;
  text-align: center;
}

.popup-content h3 {
  margin: 0 0 16px 0;
  font-size: 0.95rem;
  white-space: pre-wrap;
}

.popup-content button {
  padding: 10px 24px;
  background: #2196f3;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
</style>
